import React, { useState, useEffect, useCallback } from 'react';
import { logError } from '@/lib/logger';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import {
  AdminHeader,
  MetricsView,
  UsersView,
  PromptsView,
  ModerationView,
  ModelsView,
  ChurnView,
  DangerZoneView
} from '@/components/admin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ensure axios sends cookies
axios.defaults.withCredentials = true;

export const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('metrics');
  
  // Weekly prompt state
  const [currentPrompt, setCurrentPrompt] = useState(null);
  
  // Moderation state
  const [reports, setReports] = useState([]);
  const [reportedContent, setReportedContent] = useState({});

  const fetchData = useCallback(async () => {
    try {
      // Cookies are automatically sent with credentials: true
      const [metricsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/metrics`),
        axios.get(`${API}/admin/users`, { params: { limit: 200 } })
      ]);

      setMetrics(metricsRes.data);
      setUsers(usersRes.data.users);
      
      // Fetch current weekly prompt - non-critical
      try {
        const promptRes = await axios.get(`${API}/admin/weekly-prompts`);
        setCurrentPrompt(promptRes.data.current_prompt);
      } catch (promptError) {
        // Prompt endpoint may not exist yet - continue without it
                  logError('Weekly prompt fetch failed:', promptError?.response?.status);
      }
      
      // Fetch moderation reports - non-critical
      try {
        const reportsRes = await axios.get(`${API}/admin/circle-reports`);
        setReports(reportsRes.data.reports || []);
      } catch (reportsError) {
        // Reports endpoint may not exist yet - continue without it
                  logError('Reports fetch failed:', reportsError?.response?.status);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (logoutError) {
      // Logout may fail - proceed with navigation anyway
              logError('Logout request failed:', logoutError?.response?.status);
    }
    navigate('/admin/login');
  }, [navigate]);

  const handlePromptCreated = useCallback(async () => {
    try {
      const promptRes = await axios.get(`${API}/admin/weekly-prompts`);
      setCurrentPrompt(promptRes.data.current_prompt);
    } catch (error) {
      // Refresh failure is non-critical
              logError('Prompt refresh failed:', error?.response?.status);
    }
  }, []);

  const handleModerationActionTaken = useCallback(async () => {
    try {
      const reportsRes = await axios.get(`${API}/admin/circle-reports`);
      setReports(reportsRes.data.reports || []);
    } catch (error) {
      // Refresh failure is non-critical
              logError('Reports refresh failed:', error?.response?.status);
    }
  }, []);

  const handleFetchContent = useCallback((contentId, content) => {
    setReportedContent(prev => ({ ...prev, [contentId]: content }));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center">
        <div className="animate-pulse text-stone-600">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F6]" data-testid="admin-dashboard">
      <AdminHeader 
        view={view}
        setView={setView}
        reportsCount={reports.length}
        onLogout={handleLogout}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-6 sm:py-12">
        {view === 'metrics' && <MetricsView metrics={metrics} />}
        {view === 'users' && <UsersView users={users} />}
        {view === 'prompts' && (
          <PromptsView 
            currentPrompt={currentPrompt} 
            onPromptCreated={handlePromptCreated} 
          />
        )}
        {view === 'moderation' && (
          <ModerationView 
            reports={reports}
            reportedContent={reportedContent}
            onActionTaken={handleModerationActionTaken}
            onFetchContent={handleFetchContent}
          />
        )}
        {view === 'models' && <ModelsView />}
        {view === 'churn' && <ChurnView />}
        {view === 'danger' && <DangerZoneView />}
      </main>
    </div>
  );
};

export default AdminDashboardPage;
